import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { UploadProjectImage } from "../../pages/add-designer-file-page/add-project/upload-project-image";
import TinyMceEditor from "../tinymce-editor";
import { ImageFile } from "../before-after-image-galleries";

interface AdditionalSection {
  title: string;
  content: string;
}

interface BeforeAfterStoryFormProps {
  formData: {
    introduction: string;
    challenges: string;
    wishList: string[];
    overview: string;
    additionalSections: AdditionalSection[];
    beforeImages: ImageFile[];
    afterImages: ImageFile[];
    coverImage: ImageFile | null;
  };
  onFormDataChange: (field: string, value: any) => void;
  onImageUpload?: (files: FileList) => Promise<ImageFile[]>;
  onCoverImageUpload: (image: { file: File; local_url: string }) => void;
  onCoverImageRemove: () => void;
  isCoverUploading?: boolean;
}

const CHALLENGE_IMAGE_ATTR = 'data-challenge-image="true"';

const sectionCardSx = {
  backgroundColor: "#F5F7F8",
  borderRadius: "15px",
  boxShadow: "none",
  my: 2,
  overflow: "hidden",
  "&:before": { display: "none" },
  "&:first-of-type": { borderTopLeftRadius: "15px", borderTopRightRadius: "15px" },
  "&:last-of-type": { borderBottomLeftRadius: "15px", borderBottomRightRadius: "15px" },
  "&.MuiAccordion-root.Mui-expanded": { margin: "16px 0" },
};

const sectionSummarySx = {
  px: { xs: 2, md: 3 },
  pt: { xs: 2, md: 3 },
};

const sectionDetailsSx = {
  px: { xs: 2, md: 3 },
  pb: { xs: 2, md: 3 },
};

const imageRemoveButtonSx = {
  position: "absolute",
  top: 8,
  right: 8,
  backgroundColor: "#fff",
  border: "1px solid #E0E0E0",
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
};

const extractChallengeImage = (html: string): string | null => {
  const match = html.match(/<img[^>]*data-challenge-image="true"[^>]*src="([^"]+)"[^>]*>/i);
  return match?.[1] || null;
};

const removeChallengeImage = (html: string): string =>
  html
    .replace(/<p>\s*<img[^>]*data-challenge-image="true"[^>]*>\s*<\/p>/gi, "")
    .replace(/<img[^>]*data-challenge-image="true"[^>]*>/gi, "");

const prependChallengeImage = (html: string, url: string): string =>
  `<p><img ${CHALLENGE_IMAGE_ATTR} src="${url}" alt="Challenge image" /></p>${removeChallengeImage(html)}`;

export const BeforeAfterStoryForm: React.FC<BeforeAfterStoryFormProps> = ({
  formData,
  onFormDataChange,
  onImageUpload,
  onCoverImageUpload,
  onCoverImageRemove,
  isCoverUploading = false,
}) => {
  const {
    introduction,
    challenges,
    wishList,
    overview,
    additionalSections,
    beforeImages,
    afterImages,
    coverImage,
  } = formData;

  const [newWishItem, setNewWishItem] = useState("");
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [newSection, setNewSection] = useState<AdditionalSection>({
    title: "",
    content: "",
  });
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [pairDescriptions, setPairDescriptions] = useState<string[]>([]);
  const [pairCount, setPairCount] = useState(
    Math.max(beforeImages.length, afterImages.length, 1)
  );

  useEffect(() => {
    setPairCount(Math.max(beforeImages.length, afterImages.length, 1));
  }, [beforeImages.length, afterImages.length]);

  useEffect(() => {
    setPairDescriptions((prev) => {
      const next = [...prev];
      beforeImages.forEach((img, index) => {
        if (img?.description) {
          next[index] = img.description;
        }
      });
      return next;
    });
  }, [beforeImages]);

  const challengeImageUrl = useMemo(
    () => extractChallengeImage(challenges || ""),
    [challenges]
  );
  const challengeEditorValue = useMemo(
    () => removeChallengeImage(challenges || ""),
    [challenges]
  );

  const handleChallengesChange = useCallback(
    (html: string) => {
      const sanitized = removeChallengeImage(html || "");
      const nextValue = challengeImageUrl
        ? prependChallengeImage(sanitized, challengeImageUrl)
        : sanitized;
      if (nextValue === challenges) {
        return;
      }
      onFormDataChange("challenges", nextValue);
    },
    [challengeImageUrl, challenges, onFormDataChange]
  );

  const handleAddWishItem = useCallback(() => {
    if (newWishItem.trim()) {
      onFormDataChange("wishList", [...wishList, newWishItem.trim()]);
      setNewWishItem("");
    }
  }, [newWishItem, wishList, onFormDataChange]);

  const handleRemoveWishItem = useCallback(
    (index: number) => {
      const updated = wishList.filter((_, i) => i !== index);
      onFormDataChange("wishList", updated);
    },
    [wishList, onFormDataChange]
  );

  const handleAddSection = useCallback(() => {
    if (newSection.title.trim() && newSection.content.trim()) {
      onFormDataChange("additionalSections", [...additionalSections, newSection]);
      setNewSection({ title: "", content: "" });
      setIsAddingSection(false);
    }
  }, [newSection, additionalSections, onFormDataChange]);

  const handleEditSection = useCallback(
    (index: number) => {
      setEditingSectionIndex(index);
      setNewSection(additionalSections[index]);
      setIsAddingSection(true);
    },
    [additionalSections]
  );

  const handleUpdateSection = useCallback(() => {
    if (editingSectionIndex !== null && newSection.title.trim() && newSection.content.trim()) {
      const updated = [...additionalSections];
      updated[editingSectionIndex] = newSection;
      onFormDataChange("additionalSections", updated);
      setEditingSectionIndex(null);
      setNewSection({ title: "", content: "" });
      setIsAddingSection(false);
    }
  }, [editingSectionIndex, newSection, additionalSections, onFormDataChange]);

  const handleDeleteSection = useCallback(
    (index: number) => {
      const updated = additionalSections.filter((_, i) => i !== index);
      onFormDataChange("additionalSections", updated);
    },
    [additionalSections, onFormDataChange]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingSectionIndex(null);
    setNewSection({ title: "", content: "" });
    setIsAddingSection(false);
  }, []);

  const handlePairDescriptionChange = useCallback(
    (index: number, html: string) => {
      setPairDescriptions((prev) => {
        const next = [...prev];
        next[index] = html;
        return next;
      });

      const target = beforeImages[index];
      if (target?._id) {
        const updatedBefore = [...beforeImages];
        updatedBefore[index] = { ...target, description: html || undefined };
        onFormDataChange("beforeImages", updatedBefore);
      }
    },
    [beforeImages, onFormDataChange]
  );

  const handlePairImageUpload = useCallback(
    async (index: number, type: "before" | "after", image: { file: File; local_url: string }) => {
      if (!onImageUpload) return;
      const dt = new DataTransfer();
      dt.items.add(image.file);
      try {
        const uploaded = await onImageUpload(dt.files);
        const uploadedImage = uploaded?.[0];
        if (!uploadedImage?.url) return;

        if (type === "before") {
          const updated = [...beforeImages];
          const description = pairDescriptions[index];
          updated[index] = {
            ...uploadedImage,
            description: description || uploadedImage.description,
          };
          onFormDataChange("beforeImages", updated);
        } else {
          const updated = [...afterImages];
          updated[index] = uploadedImage;
          onFormDataChange("afterImages", updated);
        }
      } catch (error) {}
    },
    [afterImages, beforeImages, onFormDataChange, onImageUpload, pairDescriptions]
  );

  const handleRemovePairImage = useCallback(
    (index: number, type: "before" | "after") => {
      const emptyImage = { _id: "", url: "" };
      if (type === "before") {
        const updated = [...beforeImages];
        updated[index] = emptyImage;
        onFormDataChange("beforeImages", updated);
        return;
      }
      const updated = [...afterImages];
      updated[index] = emptyImage;
      onFormDataChange("afterImages", updated);
    },
    [afterImages, beforeImages, onFormDataChange]
  );

  const handleRemovePair = useCallback(
    (index: number) => {
      if (pairCount <= 1) return;

      if (index < beforeImages.length) {
        const updatedBefore = [...beforeImages];
        updatedBefore.splice(index, 1);
        onFormDataChange("beforeImages", updatedBefore);
      }

      if (index < afterImages.length) {
        const updatedAfter = [...afterImages];
        updatedAfter.splice(index, 1);
        onFormDataChange("afterImages", updatedAfter);
      }

      setPairDescriptions((prev) => {
        const next = [...prev];
        if (index < next.length) {
          next.splice(index, 1);
        }
        return next;
      });

      setPairCount((prev) => Math.max(1, prev - 1));
    },
    [afterImages, beforeImages, onFormDataChange, pairCount]
  );

  const handleChallengeImageUpload = useCallback(
    async (image: { file: File; local_url: string }) => {
      if (!onImageUpload) return;
      const dt = new DataTransfer();
      dt.items.add(image.file);
      try {
        const uploaded = await onImageUpload(dt.files);
        const uploadedImage = uploaded?.[0];
        if (!uploadedImage?.url) return;
        const updatedChallenges = prependChallengeImage(challenges || "", uploadedImage.url);
        onFormDataChange("challenges", updatedChallenges);
      } catch (error) {}
    },
    [challenges, onFormDataChange, onImageUpload]
  );

  const handleChallengeImageRemove = useCallback(() => {
    const updatedChallenges = removeChallengeImage(challenges || "");
    onFormDataChange("challenges", updatedChallenges);
  }, [challenges, onFormDataChange]);

  return (
    <Stack spacing={4}>
      <Accordion defaultExpanded disableGutters elevation={0} sx={sectionCardSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Introduction
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start with the finished project photo &amp; overview
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={sectionDetailsSx}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Hero Image (Finished Project) *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is the first thing visitors see. Choose your best shot.
              </Typography>
              {coverImage ? (
                <Box
                  sx={{
                    position: "relative",
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={coverImage.url}
                    alt="Hero preview"
                    sx={{ width: "100%", maxHeight: 260, objectFit: "cover" }}
                  />
                  <IconButton
                    onClick={onCoverImageRemove}
                    size="small"
                    sx={imageRemoveButtonSx}
                    aria-label="Remove hero image"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <UploadProjectImage
                  onComplete={onCoverImageUpload}
                  multiple={false}
                  maxCount={1}
                  currentCount={coverImage ? 1 : 0}
                  sx={{ width: "100%" }}
                />
              )}
              {isCoverUploading && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Uploading hero image...
                </Typography>
              )}
            </Box>

            <TinyMceEditor
              label="Introduction Text"
              value={introduction}
              onChange={(html) => onFormDataChange("introduction", html)}
              onImageUpload={onImageUpload}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={sectionCardSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Challenges
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Describe obstacles and how you overcame them
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={sectionDetailsSx}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Challenge Image (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Show the problem area or constraint you faced.
              </Typography>
              {challengeImageUrl ? (
                <Box
                  sx={{
                    position: "relative",
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={challengeImageUrl}
                    alt="Challenge preview"
                    sx={{ width: "100%", maxHeight: 240, objectFit: "cover" }}
                  />
                  <IconButton
                    onClick={handleChallengeImageRemove}
                    size="small"
                    sx={imageRemoveButtonSx}
                    aria-label="Remove challenge image"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <UploadProjectImage
                  onComplete={handleChallengeImageUpload}
                  multiple={false}
                  maxCount={1}
                  currentCount={challengeImageUrl ? 1 : 0}
                  sx={{ width: "100%" }}
                />
              )}
            </Box>

            <TinyMceEditor
              label="Challenge Description"
              value={challengeEditorValue}
              onChange={handleChallengesChange}
              onImageUpload={onImageUpload}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={sectionCardSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Client Wish List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Key requirements and must-haves from your client
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={sectionDetailsSx}>
          <Stack spacing={2}>
            {wishList.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  border: "1px solid #E0E0E0",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckIcon sx={{ fontSize: 20, color: "#1B1B1B" }} />
                  <Typography variant="body1">{item}</Typography>
                </Box>
                <IconButton
                  onClick={() => handleRemoveWishItem(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <TextField
              id="wish-list-input"
              name="wish-list-input"
              value={newWishItem}
              onChange={(e) => setNewWishItem(e.target.value)}
              placeholder="Enter wish list item"
              fullWidth
              size="small"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddWishItem();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CheckIcon sx={{ fontSize: 20, color: "#1B1B1B" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setNewWishItem("")}
                      disabled={!newWishItem}
                      aria-label="Clear wish list input"
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "15px",
                  backgroundColor: "#fff",
                  minHeight: "48px",
                  alignItems: "center",
                  "& fieldset": { borderColor: "#9AAAF8" },
                  "&:hover fieldset": { borderColor: "#7E92F6" },
                  "&.Mui-focused fieldset": { borderColor: "#7E92F6" },
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.25,
                },
              }}
            />
            <Button
              onClick={handleAddWishItem}
              variant="outlined"
              disabled={!newWishItem.trim()}
              sx={{
                borderColor: "#E25A8A",
                borderStyle: "dashed",
                borderWidth: "1px",
                borderRadius: "15px",
                color: "#C2185B",
                width: "100%",
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#C2185B",
                  backgroundColor: "rgba(194, 24, 91, 0.04)",
                },
              }}
            >
              + Add Wish List Item
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={sectionCardSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Before &amp; After Photos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Show the transformation with side-by-side comparisons
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={sectionDetailsSx}>
          <Stack spacing={3}>
            <TinyMceEditor
              label="Overview Text (Optional)"
              value={overview}
              onChange={(html) => onFormDataChange("overview", html)}
              onImageUpload={onImageUpload}
            />

            {Array.from({ length: pairCount }).map((_, index) => {
              const beforeImage = beforeImages[index];
              const afterImage = afterImages[index];
              return (
                <Box
                  key={`pair-${index}`}
                  sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Stack spacing={2}>
                    {pairCount > 1 && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="text"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleRemovePair(index)}
                          sx={{ textTransform: "none" }}
                        >
                          Remove Pair
                        </Button>
                      </Box>
                    )}
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Before Image *
                        </Typography>
                        {beforeImage?.url ? (
                          <Box
                            sx={{
                              position: "relative",
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              component="img"
                              src={beforeImage.url}
                              alt="Before"
                              sx={{ width: "100%", height: 180, objectFit: "cover" }}
                            />
                            <IconButton
                              onClick={() => handleRemovePairImage(index, "before")}
                              size="small"
                              sx={imageRemoveButtonSx}
                              aria-label="Remove before image"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <UploadProjectImage
                            onComplete={(image: { file: File; local_url: string }) =>
                              handlePairImageUpload(index, "before", image)
                            }
                            multiple={false}
                            maxCount={1}
                            currentCount={beforeImage?.url ? 1 : 0}
                            sx={{ width: "100%" }}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          After Image *
                        </Typography>
                        {afterImage?.url ? (
                          <Box
                            sx={{
                              position: "relative",
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              component="img"
                              src={afterImage.url}
                              alt="After"
                              sx={{ width: "100%", height: 180, objectFit: "cover" }}
                            />
                            <IconButton
                              onClick={() => handleRemovePairImage(index, "after")}
                              size="small"
                              sx={imageRemoveButtonSx}
                              aria-label="Remove after image"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <UploadProjectImage
                            onComplete={(image: { file: File; local_url: string }) =>
                              handlePairImageUpload(index, "after", image)
                            }
                            multiple={false}
                            maxCount={1}
                            currentCount={afterImage?.url ? 1 : 0}
                            sx={{ width: "100%" }}
                          />
                        )}
                      </Box>
                    </Stack>

                    <TinyMceEditor
                      label="Description"
                      value={pairDescriptions[index] || ""}
                      onChange={(html) => handlePairDescriptionChange(index, html)}
                      onImageUpload={onImageUpload}
                    />
                  </Stack>
                </Box>
              );
            })}

            <Button
              variant="outlined"
              onClick={() => {
                setPairCount((prev) => prev + 1);
                setPairDescriptions((prev) => [...prev, ""]);
              }}
              sx={{
                borderColor: "#E25A8A",
                borderStyle: "dashed",
                borderWidth: "1px",
                borderRadius: "15px",
                color: "#C2185B",
                width: "100%",
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#C2185B",
                  backgroundColor: "rgba(194, 24, 91, 0.04)",
                },
              }}
            >
              + Add Another Before and After Pair
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Box>
        <Button
          variant="contained"
          onClick={() => setIsAddingSection(true)}
          sx={{ backgroundColor: "#000", color: "#fff" }}
        >
          + Add New Custom Section
        </Button>
      </Box>

      {isAddingSection && (
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            backgroundColor: "#fafafa",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            New Section Title
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Section title"
              value={newSection.title}
              onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
              fullWidth
              variant="outlined"
            />

            <TinyMceEditor
              label="Section Content"
              value={newSection.content}
              onChange={(html) => setNewSection({ ...newSection, content: html })}
              onImageUpload={onImageUpload}
            />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                onClick={editingSectionIndex !== null ? handleUpdateSection : handleAddSection}
                variant="contained"
              >
                {editingSectionIndex !== null ? "Save Section" : "Save Section"}
              </Button>
              <Button onClick={handleCancelEdit} variant="outlined">
                Cancel
              </Button>
            </Box>
          </Stack>
        </Box>
      )}

      {additionalSections.length > 0 && (
        <Stack spacing={2}>
          {additionalSections.map((section, index) => (
            <Box
              key={`${section.title}-${index}`}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {section.title}
                </Typography>
                <Box>
                  <IconButton
                    onClick={() => handleEditSection(index)}
                    size="small"
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteSection(index)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <span dangerouslySetInnerHTML={{ __html: section.content }} />
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default BeforeAfterStoryForm;
